import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Send, CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import { formatApiError } from "../AuthContext";
import { useLang } from "../LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const VALID_SUBJECTS = ["general", "inscription", "benevolat", "immersion", "partenariat", "soutien", "autre"];
  const rawSubject = params.get("sujet");
  const defaultSubject = VALID_SUBJECTS.includes(rawSubject) ? rawSubject : "general";

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: defaultSubject,
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setStatus("sent");
      setForm({ name: "", email: "", subject: defaultSubject, message: "" });
    } catch (err) {
      setStatus("error");
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="container-page pt-16 sm:pt-24 pb-10">
        <p className="eyebrow mb-4">{t("contact.eyebrow")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-ink max-w-3xl leading-tight">
          {t("contact.title")}
        </h1>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-2xl">
          {t("contact.intro")}
        </p>
      </section>

      <section className="container-page py-10">
        <div className="grid lg:grid-cols-12 gap-7">
          <aside className="lg:col-span-4 space-y-5">
            <div className="rounded-3xl bg-forest-light/50 border border-forest-light p-7" data-testid="contact-note">
              <p className="text-sm text-forest-deep leading-relaxed">
                {t("contact.note")}
              </p>
            </div>
            <div className="card-soft p-7">
              <Mail size={20} className="text-forest mb-3" />
              <h3 className="font-heading text-base font-semibold text-ink mb-1">
                {t("contact.email_title")}
              </h3>
              <a
                href="mailto:contact@ecolepleinenature.org"
                className="text-ink-muted hover:text-forest"
                data-testid="contact-email-link"
              >
                contact@ecolepleinenature.org
              </a>
            </div>
            <div className="card-soft p-7">
              <MapPin size={20} className="text-forest mb-3" />
              <h3 className="font-heading text-base font-semibold text-ink mb-1">
                {t("contact.address_title")}
              </h3>
              <p className="text-ink-muted text-sm leading-relaxed">
                {t("pratique.address_l1")}<br />{t("pratique.address_l2")}<br />{t("pratique.address_l3")}
              </p>
              <p className="text-ink-muted text-sm mt-3 flex items-center gap-2">
                <Phone size={15} className="text-forest" />
                <a href="tel:+33980615298" className="hover:text-forest" data-testid="contact-phone-link">
                  {t("pratique.phone")}
                </a>
              </p>
            </div>
            <div className="card-soft overflow-hidden p-0" data-testid="contact-map">
              <iframe
                title="École Pleine Nature — carte"
                src="https://www.google.com/maps?q=45+route+de+Mont%C3%A9gut+Lamb%C3%A8ge+09200+Moulis&output=embed"
                className="w-full h-56 border-0 block"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </aside>

          <div className="lg:col-span-8">
            <form
              onSubmit={onSubmit}
              className="card-soft p-7 sm:p-10 space-y-5"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">
                    {t("contact.form_name")}
                  </label>
                  <input
                    name="name"
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={onChange}
                    className="field"
                    placeholder={t("contact.form_name_placeholder")}
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">
                    {t("contact.form_email")}
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    className="field"
                    placeholder={t("contact.form_email_placeholder")}
                    data-testid="contact-input-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">
                  {t("contact.form_subject")}
                </label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  className="field"
                  data-testid="contact-select-subject"
                >
                  <option value="general">{t("contact.subjects.general")}</option>
                  <option value="inscription">{t("contact.subjects.inscription")}</option>
                  <option value="benevolat">{t("contact.subjects.benevolat")}</option>
                  <option value="immersion">{t("contact.subjects.immersion")}</option>
                  <option value="partenariat">{t("contact.subjects.partenariat")}</option>
                  <option value="soutien">{t("contact.subjects.soutien")}</option>
                  <option value="autre">{t("contact.subjects.autre")}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">
                  {t("contact.form_message")}
                </label>
                <textarea
                  name="message"
                  required
                  minLength={10}
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  className="field resize-y"
                  placeholder={t("contact.form_message_placeholder")}
                  data-testid="contact-input-message"
                />
              </div>

              {status === "error" && (
                <div className="rounded-2xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="contact-error">
                  {error}
                </div>
              )}
              {status === "sent" && (
                <div
                  className="rounded-2xl bg-forest-light/60 border border-forest-light text-forest-deep px-4 py-3 text-sm flex items-center gap-2"
                  data-testid="contact-success"
                >
                  <CheckCircle2 size={18} className="text-forest" />
                  {t("contact.success")}
                </div>
              )}

              <div className="flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-ink-muted">{t("contact.privacy")}</p>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-primary disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {status === "sending" ? t("contact.form_sending") : t("contact.form_send")} <Send size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
